<template>
  <div class="language-select">
    <div class="step-badge">
      <i class="el-icon-chat-line-square"></i>
      <span>{{ $t('buy.steps.badge.step2') }}</span>
    </div>

    <div class="section-header">
      <h3>{{ $t('buy.languages.title') }}</h3>
      <p class="section-desc">{{ $t('buy.languages.subtitle') }}</p>
    </div>

    <div class="language-summary">
      <div class="summary-left">
        <i class="el-icon-collection-tag"></i>
        <span>{{ selectedCountLabel }}</span>
      </div>
      <div class="summary-right">
        <el-tag effect="plain" type="info">{{ planLimitLabel }}</el-tag>
      </div>
    </div>

    <div class="language-grid">
      <button
        v-for="language in languages"
        :key="language.id"
        type="button"
        class="language-card"
        :class="{ selected: isSelected(language.id), disabled: isLanguageDisabled(language.id) }"
        @click="toggleLanguage(language)"
      >
        <span class="language-name">{{ languageLabel(language) }}</span>
        <span class="language-code">{{ language.code }}</span>
        <span class="check-indicator" :class="{ checked: isSelected(language.id) }">
          <i class="el-icon-check"></i>
        </span>
      </button>
    </div>

    <div class="info-banner" :class="{ warning: hasLimitWarning }">
      <i :class="hasLimitWarning ? 'el-icon-warning-outline' : 'el-icon-info'"></i>
      <span>{{ helperText }}</span>
    </div>
  </div>
</template>

<script>
const FALLBACK_LIMITS = {
  starter: 1,
  professional: 2,
  enterprise: null
}

const FALLBACK_LANGUAGES = [
  { id: 'english', code: 'EN' },
  { id: 'french', code: 'FR' },
  { id: 'spanish', code: 'ES' },
  { id: 'chinese', code: 'ZH' },
  { id: 'german', code: 'DE' },
  { id: 'polish', code: 'PL' },
  { id: 'czech', code: 'CS' },
  { id: 'slovak', code: 'SK' },
  { id: 'hungarian', code: 'HU' },
  { id: 'romanian', code: 'RO' },
  { id: 'bulgarian', code: 'BG' },
  { id: 'serbian', code: 'SR' },
  { id: 'croatian', code: 'HR' },
  { id: 'slovenian', code: 'SL' }
]

export default {
  name: 'LanguageSelect',
  props: {
    purchaseConfig: {
      type: Object,
      default: null
    },
    planId: {
      type: String,
      default: 'professional'
    },
    selectedValue: {
      type: Array,
      default: null
    }
  },
  data() {
    return {
      selectedLanguages: ['english'],
      lastEmittedSelectionKey: ''
    }
  },
  computed: {
    languages() {
      const configLanguages = this.purchaseConfig && Array.isArray(this.purchaseConfig.languages)
        ? this.purchaseConfig.languages
        : []
      const source = configLanguages.length ? configLanguages : FALLBACK_LANGUAGES
      return source.map(item => ({
        id: item.id || item.code,
        code: String(item.code || item.id || '').toUpperCase(),
        name: item.name || item.id || item.code
      }))
    },
    languageLimit() {
      const plan = this.purchaseConfig && this.purchaseConfig.plans && this.purchaseConfig.plans[this.planId]
      if (plan && Object.prototype.hasOwnProperty.call(plan, 'languageLimit')) {
        return plan.languageLimit
      }
      return Object.prototype.hasOwnProperty.call(FALLBACK_LIMITS, this.planId)
        ? FALLBACK_LIMITS[this.planId]
        : 1
    },
    isUnlimited() {
      return this.languageLimit == null || Number(this.languageLimit) <= 0
    },
    selectedCountLabel() {
      return this.$t('buy.languages.selectedCount', { count: this.selectedLanguages.length })
    },
    planLimitLabel() {
      if (this.isUnlimited) {
        return this.$t('buy.languages.limitUnlimited')
      }
      return this.$t('buy.languages.limitCount', { count: this.languageLimit })
    },
    hasLimitWarning() {
      return !this.isUnlimited && this.selectedLanguages.length >= Number(this.languageLimit)
    },
    helperText() {
      if (this.isUnlimited) {
        return this.$t('buy.languages.infoUnlimited')
      }
      return this.$t('buy.languages.infoLimited', { count: this.languageLimit })
    }
  },
  methods: {
    isSelected(id) {
      return this.selectedLanguages.includes(id)
    },
    isLanguageDisabled(id) {
      const limit = Number(this.languageLimit)
      if (!this.isUnlimited && limit <= 1) {
        return false
      }
      return !this.isSelected(id) && !this.isUnlimited && this.selectedLanguages.length >= limit
    },
    languageLabel(language) {
      const label = this.$t(`buy.languages.items.${language.id}`)
      return label === `buy.languages.items.${language.id}` ? language.name || language.id : label
    },
    toggleLanguage(language) {
      if (this.isSelected(language.id)) {
        if (this.selectedLanguages.length <= 1) {
          this.$message.warning(this.$t('buy.languages.minimumRequired'))
          return
        }
        this.selectedLanguages = this.selectedLanguages.filter(id => id !== language.id)
      } else {
        if (this.isLanguageDisabled(language.id)) {
          this.$message.warning(this.$t('buy.languages.limitReached', { count: this.languageLimit }))
          return
        }
        if (!this.isUnlimited && this.selectedLanguages.length >= Number(this.languageLimit)) {
          this.selectedLanguages = [language.id]
          this.emitSelection()
          return
        }
        this.selectedLanguages = [...this.selectedLanguages, language.id]
      }
      this.emitSelection()
    },
    isSameSelection(first, second) {
      if (!Array.isArray(first) || !Array.isArray(second)) return false
      return first.length === second.length && first.every((item, index) => item === second[index])
    },
    setSelectedLanguages(languages) {
      if (this.isSameSelection(this.selectedLanguages, languages)) {
        return false
      }
      this.selectedLanguages = [...languages]
      return true
    },
    getDefaultLanguageId() {
      const firstLanguage = this.languages[0]
      return firstLanguage ? firstLanguage.id : 'english'
    },
    normalizeSelection(value) {
      let languages = Array.isArray(value) && value.length ? [...value] : [this.getDefaultLanguageId()]
      if (!this.isUnlimited && languages.length > Number(this.languageLimit)) {
        languages = languages.slice(0, Number(this.languageLimit))
      }
      return languages.length ? languages : [this.getDefaultLanguageId()]
    },
    applySelectedValue() {
      const source = Array.isArray(this.selectedValue) && this.selectedValue.length
        ? this.selectedValue
        : this.selectedLanguages
      return this.setSelectedLanguages(this.normalizeSelection(source))
    },
    trimToPlanLimit() {
      return this.setSelectedLanguages(this.normalizeSelection(this.selectedLanguages))
    },
    emitSelection() {
      const valid = this.selectedLanguages.length > 0
      this.$emit('validate', valid)
      const payload = {
        languages: [...this.selectedLanguages],
        languageLimit: this.isUnlimited ? null : Number(this.languageLimit)
      }
      const payloadKey = JSON.stringify(payload)
      if (payloadKey === this.lastEmittedSelectionKey) {
        return
      }
      this.lastEmittedSelectionKey = payloadKey
      this.$emit('languages-selected', payload)
    }
  },
  created() {
    this.applySelectedValue()
    this.emitSelection()
  },
  watch: {
    selectedValue: {
      handler() {
        this.applySelectedValue()
        this.$emit('validate', this.selectedLanguages.length > 0)
      },
      deep: true
    },
    planId() {
      this.trimToPlanLimit()
      this.emitSelection()
    },
    purchaseConfig: {
      handler() {
        this.trimToPlanLimit()
        this.emitSelection()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.language-select {
  max-width: 980px;
  margin: 0 auto;
}

.step-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 20px;
  background: linear-gradient(135deg, #6366f1, #818cf8);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 24px;

  i {
    font-size: 14px;
  }
}

.section-header {
  text-align: center;
  margin-bottom: 30px;

  h3 {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px;
  }

  .section-desc {
    color: #64748b;
    font-size: 15px;
    margin: 0;
  }
}

.language-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: linear-gradient(135deg, #f0f4ff, #ede9fe);
  border-radius: 12px;
  margin-bottom: 24px;

  .summary-left {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #4f46e5;
    font-weight: 600;
    font-size: 14px;

    i {
      font-size: 18px;
    }
  }
}

.language-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.language-card {
  position: relative;
  min-height: 92px;
  padding: 18px 46px 18px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: all 0.25s ease;

  &:hover {
    border-color: #c7d2fe;
    box-shadow: 0 4px 16px rgba(99,102,241,0.08);
    transform: translateY(-2px);
  }

  &.selected {
    border-color: #6366f1;
    background: linear-gradient(135deg, #faf5ff 0%, #fff 100%);
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.56;
    transform: none;
    box-shadow: none;
  }
}

.language-name,
.language-code {
  display: block;
}

.language-name {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.language-code {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.08em;
}

.check-indicator {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease;

  i {
    color: #fff;
    font-size: 14px;
    opacity: 0;
  }

  &.checked {
    background: #6366f1;
    border-color: #6366f1;

    i {
      opacity: 1;
    }
  }
}

.info-banner {
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  color: #2563eb;
  font-size: 14px;

  &.warning {
    background: #fff7ed;
    border-color: #fed7aa;
    color: #c2410c;
  }
}

@media screen and (max-width: 900px) {
  .language-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 560px) {
  .language-select {
    max-width: 100%;
  }

  .step-badge {
    padding: 5px 12px;
    margin-bottom: 16px;
    font-size: 12px;
  }

  .section-header {
    text-align: left;
    margin-bottom: 18px;

    h3 {
      font-size: 20px;
    }

    .section-desc {
      font-size: 13px;
      line-height: 1.5;
    }
  }

  .language-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .language-summary {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 10px;
    margin-bottom: 16px;
  }

  .language-card {
    min-height: 70px;
    padding: 14px 42px 14px 14px;
    border-width: 1px;
    border-radius: 12px;

    &:hover {
      transform: none;
    }
  }

  .language-name {
    font-size: 15px;
  }

  .language-code {
    margin-top: 5px;
  }

  .check-indicator {
    top: 14px;
    right: 14px;
  }

  .info-banner {
    align-items: flex-start;
    margin-top: 16px;
    padding: 12px 14px;
    font-size: 13px;
  }
}
</style>
